import React from 'react'
import { useAuth } from '../context/AuthContext'
import { recurringTaskService } from '../services/recurringTaskService'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import {
  CalendarDays,
  Plus,
  Trash2,
  Clock,
  ExternalLink,
  Pause,
  Play,
  Zap,
  Pencil,
} from 'lucide-react'
import {
  RecurringSchedule,
  BlitzAutoSyncRule,
  RecurringTaskExecution,
  FREQUENCY_OPTIONS,
} from '../types/recurringTasks'
import { TASK_TYPES } from '../features/recurringTasks/taskTypeRegistry'

interface RecurringTasksTabProps {
  customerId: string
  customerName: string
}

export function RecurringTasksTab({ customerId, customerName }: RecurringTasksTabProps) {
  const { partner } = useAuth()
  const partnerId = partner?.partnerId || ''

  const [schedules, setSchedules] = React.useState<RecurringSchedule[]>([])
  const [executions, setExecutions] = React.useState<RecurringTaskExecution[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  // Dialog state
  const [showAddDialog, setShowAddDialog] = React.useState(false)
  const [newTaskType, setNewTaskType] = React.useState('BlitzReport')
  const [newDayOfMonth, setNewDayOfMonth] = React.useState(15)
  const [newFrequency, setNewFrequency] = React.useState('Monthly')
  const [newCidn, setNewCidn] = React.useState('')
  const [isSaving, setIsSaving] = React.useState(false)

  // Edit-CIDN dialog state
  const [editingSchedule, setEditingSchedule] = React.useState<RecurringSchedule | null>(null)
  const [editCidn, setEditCidn] = React.useState('')
  const [isEditSaving, setIsEditSaving] = React.useState(false)

  const loadData = React.useCallback(async () => {
    if (!partnerId || !customerId) return
    setIsLoading(true)
    setError(null)

    try {
      const [schedulesRes, executionsRes] = await Promise.all([
        recurringTaskService.getSchedules(partnerId, customerId),
        recurringTaskService.getExecutions(partnerId, customerId),
      ])

      if (schedulesRes.success && schedulesRes.data) setSchedules(schedulesRes.data)
      if (executionsRes.success && executionsRes.data) setExecutions(executionsRes.data)
    } catch (err) {
      setError('Failed to load recurring tasks')
    } finally {
      setIsLoading(false)
    }
  }, [partnerId, customerId])

  React.useEffect(() => {
    loadData()
  }, [loadData])

  const handleCreateSchedule = async () => {
    if (!partnerId) return
    setIsSaving(true)
    try {
      let metadata: string | undefined
      if (newTaskType === 'BlitzReport' && newCidn.trim()) {
        metadata = JSON.stringify({ cidn: newCidn.trim() })
      }
      const res = await recurringTaskService.createSchedule(partnerId, customerId, {
        taskType: newTaskType,
        dayOfMonth: newDayOfMonth,
        frequency: newFrequency,
        metadata,
      })
      if (res.success) {
        setShowAddDialog(false)
        setNewDayOfMonth(15)
        setNewFrequency('Monthly')
        setNewCidn('')
        await loadData()
      } else {
        setError(res.error || 'Failed to create schedule')
      }
    } catch (err) {
      setError('Failed to create schedule')
    } finally {
      setIsSaving(false)
    }
  }

  const getScheduleCidn = (schedule: RecurringSchedule): string | null => {
    if (!schedule.metadata) return null
    try {
      const parsed = JSON.parse(schedule.metadata)
      const value = typeof parsed?.cidn === 'string' ? parsed.cidn.trim() : ''
      return value || null
    } catch {
      return null
    }
  }

  const getAutoSyncRule = (schedule: RecurringSchedule): BlitzAutoSyncRule | null => {
    try {
      const rule = JSON.parse(schedule.metadata || '{}').blitzAutoSync as BlitzAutoSyncRule | undefined
      return rule && rule.cidn === getScheduleCidn(schedule) && rule.customerId === customerId
        && rule.partnerId === partnerId && rule.verifiedApprovalId && rule.accountNames?.length ? rule : null
    } catch { return null }
  }

  const [syncingId, setSyncingId] = React.useState<string | null>(null)
  const handleToggleAutoSync = async (schedule: RecurringSchedule) => {
    setSyncingId(schedule.id)
    setError(null)
    try {
      const res = await recurringTaskService.setBlitzAutoSync(partnerId, customerId, schedule.id, !getAutoSyncRule(schedule)?.enabled)
      if (!res.success) throw new Error(res.error || 'Could not update auto-sync')
      await loadData()
    } catch (err: any) { setError(err?.message || 'Could not update auto-sync') }
    finally { setSyncingId(null) }
  }

  const handleOpenEdit = (schedule: RecurringSchedule) => {
    setEditingSchedule(schedule)
    setEditCidn(getScheduleCidn(schedule) ?? '')
  }

  const handleSaveEdit = async () => {
    if (!partnerId || !editingSchedule) return
    setIsEditSaving(true)
    try {
      const metadata = JSON.stringify({ ...JSON.parse(editingSchedule.metadata || '{}'), cidn: editCidn.trim() })
      const res = await recurringTaskService.updateSchedule(partnerId, customerId, editingSchedule.id, {
        metadata,
      })
      if (res.success) {
        setEditingSchedule(null)
        setEditCidn('')
        await loadData()
      } else {
        setError(res.error || 'Failed to update schedule')
      }
    } catch (err) {
      setError('Failed to update schedule')
    } finally {
      setIsEditSaving(false)
    }
  }

  const handleToggleActive = async (schedule: RecurringSchedule) => {
    if (!partnerId) return
    try {
      await recurringTaskService.updateSchedule(partnerId, customerId, schedule.id, {
        isActive: !schedule.isActive,
      })
      await loadData()
    } catch (err) {
      setError('Failed to update schedule')
    }
  }

  const handleDeleteSchedule = async (schedule: RecurringSchedule) => {
    if (!partnerId) return
    try {
      await recurringTaskService.deleteSchedule(partnerId, customerId, schedule.id)
      await loadData()
    } catch (err) {
      setError('Failed to delete schedule')
    }
  }

  const [triggeringId, setTriggeringId] = React.useState<string | null>(null)

  const handleTriggerNow = async (schedule: RecurringSchedule) => {
    if (!partnerId) return
    setTriggeringId(schedule.id)
    setError(null)
    try {
      const res = await recurringTaskService.triggerSchedule(partnerId, customerId, schedule.id)
      if (res.success) {
        await loadData()
      } else {
        setError(res.error || 'Failed to trigger schedule')
      }
    } catch (err) {
      setError('Failed to trigger schedule')
    } finally {
      setTriggeringId(null)
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'TicketCreated':
        return <Badge variant="default">Ticket Created</Badge>
      case 'PendingApproval':
        return <Badge className="bg-amber-100 text-amber-800">Pending Approval</Badge>
      case 'Approved':
        return <Badge variant="success">Approved</Badge>
      case 'AutoApproved':
        return <Badge variant="success">Auto-approved</Badge>
      case 'CompletedManually':
        return <Badge variant="success">Completed manually</Badge>
      case 'Rejected':
        return <Badge variant="destructive">Rejected</Badge>
      case 'Failed':
        return <Badge variant="destructive">Failed</Badge>
      default:
        return <Badge variant="default">{status}</Badge>
    }
  }

  const getOrdinalSuffix = (day: number) => {
    if (day > 3 && day < 21) return 'th'
    switch (day % 10) {
      case 1: return 'st'
      case 2: return 'nd'
      case 3: return 'rd'
      default: return 'th'
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 rounded-full border-4 border-slate-200 border-t-orange-600 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {/* Active Schedules */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-slate-900">Recurring Schedules</h3>
          <Button size="sm" onClick={() => setShowAddDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Schedule
          </Button>
        </div>

        {schedules.length === 0 ? (
          <div className="text-center py-8 bg-slate-50 rounded-lg border border-dashed border-slate-300">
            <CalendarDays className="h-10 w-10 text-slate-400 mx-auto mb-3" />
            <p className="text-sm text-slate-600 font-medium">No recurring schedules</p>
            <p className="text-xs text-slate-500 mt-1">Set up a recurring task to automate report generation</p>
          </div>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Task Type</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Day of Month</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Frequency</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Status</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {schedules.map((schedule) => (
                  <tr key={schedule.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-4 py-3 font-medium text-slate-900">
                      <div>{TASK_TYPES.find(t => t.value === schedule.taskType)?.label || schedule.taskType}</div>
                      {getScheduleCidn(schedule) && (
                        <div className="text-xs font-normal text-slate-500 mt-0.5">CIDN: {getScheduleCidn(schedule)}</div>
                      )}
                      {schedule.taskType === 'BlitzReport' && (
                        <div className="mt-2 text-xs font-normal text-slate-600">
                          {getAutoSyncRule(schedule) ? <>
                            <div>Approved account names: {getAutoSyncRule(schedule)!.accountNames.join(', ')}</div>
                            <button type="button" disabled={syncingId === schedule.id} onClick={() => handleToggleAutoSync(schedule)}
                              className="mt-1 text-orange-700 underline disabled:opacity-50">
                              Auto-sync {getAutoSyncRule(schedule)!.enabled ? 'on — turn off' : 'off — turn on'}
                            </button>
                          </> : <span>Auto-sync off · verify a report in Approvals first</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {schedule.dayOfMonth}{getOrdinalSuffix(schedule.dayOfMonth)}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{schedule.frequency}</td>
                    <td className="px-4 py-3">
                      <Badge variant={schedule.isActive ? 'success' : 'default'}>
                        {schedule.isActive ? 'Active' : 'Paused'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleTriggerNow(schedule)}
                          disabled={triggeringId === schedule.id}
                          className="p-1.5 text-slate-400 hover:text-orange-600 rounded-md hover:bg-orange-50 disabled:opacity-50"
                          title="Trigger now"
                        >
                          {triggeringId === schedule.id ? (
                            <div className="h-4 w-4 rounded-full border-2 border-slate-300 border-t-orange-600 animate-spin" />
                          ) : (
                            <Zap className="h-4 w-4" />
                          )}
                        </button>
                        {schedule.taskType === 'BlitzReport' && (
                          <button
                            type="button"
                            onClick={() => handleOpenEdit(schedule)}
                            className="p-1.5 text-slate-400 hover:text-slate-700 rounded-md hover:bg-slate-100"
                            title="Edit CIDN"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleToggleActive(schedule)}
                          className="p-1.5 text-slate-400 hover:text-slate-700 rounded-md hover:bg-slate-100"
                          title={schedule.isActive ? 'Pause schedule' : 'Resume schedule'}
                        >
                          {schedule.isActive ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteSchedule(schedule)}
                          className="p-1.5 text-slate-400 hover:text-rose-600 rounded-md hover:bg-rose-50"
                          title="Delete schedule"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Execution History */}
      <div>
        <h3 className="text-base font-semibold text-slate-900 mb-4">Execution History</h3>
        {executions.length === 0 ? (
          <div className="text-center py-6 bg-slate-50 rounded-lg border border-dashed border-slate-300">
            <Clock className="h-8 w-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No executions yet</p>
          </div>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Task Type</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Ticket</th>
                </tr>
              </thead>
              <tbody>
                {executions.map((exec) => (
                  <tr key={exec.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-4 py-3 text-slate-900">
                      {new Date(exec.scheduledDate).toLocaleDateString('en-AU', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {TASK_TYPES.find(t => t.value === exec.taskType)?.label || exec.taskType}
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(exec.status)}</td>
                    <td className="px-4 py-3">
                      {exec.zendeskTicketId ? (
                        <span className="inline-flex items-center gap-1 text-slate-500 text-xs font-mono">
                          #{exec.zendeskTicketId}
                          <ExternalLink className="h-3 w-3" />
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Schedule Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Recurring Schedule</DialogTitle>
            <DialogDescription>
              Set up a recurring task for {customerName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1.5">Task Type</label>
              <select
                value={newTaskType}
                onChange={(e) => setNewTaskType(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                {TASK_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {newTaskType === 'BlitzReport' && (
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1.5">CIDN</label>
                <Input
                  type="text"
                  value={newCidn}
                  onChange={(e) => setNewCidn(e.target.value)}
                  placeholder="e.g. 1234567890"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Telstra Carrier Identification Number — included on every ticket raised by this schedule.
                </p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1.5">Day of Month</label>
              <Input
                type="number"
                min={1}
                max={31}
                value={newDayOfMonth}
                onChange={(e) => setNewDayOfMonth(parseInt(e.target.value) || 1)}
              />
              <p className="text-xs text-slate-500 mt-1">
                If the month has fewer days, the task will run on the last day of the month
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1.5">Frequency</label>
              <select
                value={newFrequency}
                onChange={(e) => setNewFrequency(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                {FREQUENCY_OPTIONS.map((freq) => (
                  <option key={freq.value} value={freq.value}>
                    {freq.label}
                  </option>
                ))}
              </select>
              {newFrequency === 'Quarterly' && (
                <p className="text-xs text-slate-500 mt-1">
                  Runs in January, April, July, and October
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateSchedule}
              loading={isSaving}
              disabled={newTaskType === 'BlitzReport' && !newCidn.trim()}
            >
              Create Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit CIDN Dialog */}
      <Dialog open={editingSchedule !== null} onOpenChange={(open) => { if (!open) setEditingSchedule(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit CIDN</DialogTitle>
            <DialogDescription>
              Update the Telstra Carrier Identification Number used on tickets raised by this schedule.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1.5">CIDN</label>
              <Input
                type="text"
                value={editCidn}
                onChange={(e) => setEditCidn(e.target.value)}
                placeholder="e.g. 1234567890"
              />
              <p className="text-xs text-slate-500 mt-1">
                Changing the CIDN clears approved account names and turns auto-sync off. Verify a report for the new CIDN before enabling it again.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingSchedule(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              loading={isEditSaving}
              disabled={!editCidn.trim()}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
